import { MarketType } from 'src/solace';
import {
  GROUP,
  UserPreferences,
  METADATA_KEY,
  MetadataKeyType,
  GroupType,
  Metadata,
} from './user-preferences';

export type WatchlistItem = [issuerId: string, marketId: string];

export type WatchlistVM = {
  watchListTypeId: number;
  configuration: string;
  title: string;
  preferenceId: string;
};

export type WatchlistIssuerVM = {
  /** Watchlist Order Id */
  watchlistType: number;
  /** Ticker + Serie */
  issueID: string;
  /** Market Type */
  marketID: number;
  /** Ticker */
  symbol?: string;
  /** Serie */
  serie?: string;
  percentChange?: number;
  tipoValorIndeval?: string;
  issueName?: string;
  lastPrice?: number;
  closePrice?: number;
  sectorId?: string;
  instrumentType?: number;
  tradingLineId?: number;
  minimumAmount?: number;
  isFundOfFunds?: boolean;
};

export class Watchlist {
  private static DEFAULT_NAME = 'Watchlist';

  constructor() {}

  static getWatchlists(preferences: UserPreferences[]): WatchlistVM[] {
    return preferences
      .map((preference) => {
        const metaName = Watchlist.getMetaDataByKey(
          preference,
          METADATA_KEY.name,
        );
        const metaOrder = Watchlist.getMetaDataByKey(
          preference,
          METADATA_KEY.order,
        );

        const payload: WatchlistVM = {
          title: metaName ? metaName.value : Watchlist.DEFAULT_NAME,
          configuration: '',
          watchListTypeId: metaOrder ? Number(metaOrder.value) : 1,
          preferenceId: preference.id,
        };

        return payload;
      })
      .sort((wl1, wl2) => (wl2.watchListTypeId > wl1.watchListTypeId ? 1 : -1));
  }

  static parseIssuers(payload: string): WatchlistItem[] {
    if (!payload) {
      return [];
    }

    if (payload.indexOf(',') === -1) {
      return [payload.split('|').map((item) => item.trim())] as WatchlistItem[];
    }

    return payload
      .split(',')
      .map((issuer) => issuer.split('|').map((item) => item.trim()))
      .slice() as WatchlistItem[];
  }

  static createPreferenceGroup(
    groupId: GroupType,
    metaKey?: MetadataKeyType | Metadata<MetadataKeyType>[],
    metaValue?: string,
  ): UserPreferences {
    const group: UserPreferences = {
      group_id: groupId,
      metadata: Array.isArray(metaKey) ? metaKey : [],
    };

    if (!Array.isArray(metaKey) && metaKey && metaValue) {
      group.metadata.push({
        key: metaKey,
        value: metaValue,
      });
    }

    return group;
  }

  static addMetadata(
    metadata: Metadata<MetadataKeyType>[],
    metaKey: MetadataKeyType,
    metaValue: string,
  ): Metadata<MetadataKeyType>[] {
    return [
      ...metadata,
      {
        key: metaKey,
        value: metaValue,
      },
    ];
  }

  static getPreferenceByGroup(
    payload: UserPreferences[],
    key: GroupType,
  ): UserPreferences | undefined {
    return payload.find((group) => group.group_id === key) ?? undefined;
  }

  static getMetaValue(
    payload: UserPreferences[] | UserPreferences | undefined,
    groupId: GroupType,
    metaKey: MetadataKeyType,
  ): string | null {
    const pref = Array.isArray(payload)
      ? Watchlist.getPreferenceByGroup(payload, groupId)
      : payload;

    if (!pref) {
      return null;
    }

    const meta = Watchlist.getMetaDataByKey(pref, metaKey);

    if (!meta) {
      return null;
    }

    return meta.value;
  }

  static getMetaDataByKey(
    payload: UserPreferences,
    metaKey: MetadataKeyType,
  ): Metadata<MetadataKeyType> | undefined {
    return payload.metadata.find((meta) => meta.key === metaKey) ?? undefined;
  }

  static updateMetaData({
    metaData,
    metaKey,
    metaValue,
    addMeta,
  }: {
    metaData: Metadata<MetadataKeyType>[];
    metaKey: MetadataKeyType;
    metaValue: string;
    addMeta?: boolean;
  }): Metadata<MetadataKeyType>[] {
    const metaIndex = metaData.findIndex((meta) => meta.key === metaKey);
    if (metaIndex > -1) {
      metaData.splice(metaIndex, 1, {
        key: metaKey,
        value: `${metaValue}`,
      });
    } else if (addMeta) {
      metaData.push({
        key: metaKey,
        value: `${metaValue}`,
      });
    }
    return metaData;
  }
}
